import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
  isMobileBreakpoint,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLElement} counter - The counter element.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails', 'counter'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  // Swipe navigation state
  #touchStartX = 0;
  #touchStartY = 0;
  #swipeThreshold = 50;

  // Infinite carousel state
  /** @type {HTMLElement | null} */
  #cloneFirst = null;
  /** @type {HTMLElement | null} */
  #cloneLast = null;
  #isLooping = false;

  #SCROLL_DURATION = 500;

  connectedCallback() {
    super.connectedCallback();
    this.scrollContainer?.addEventListener('scroll', this.handleScroll);

    // Add swipe navigation for mobile
    this.refs.dialog?.addEventListener('touchstart', this.#handleSwipeStart, { passive: true });
    this.refs.dialog?.addEventListener('touchend', this.#handleSwipeEnd, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.scrollContainer?.removeEventListener('scroll', this.handleScroll);

    this.refs.dialog?.removeEventListener('touchstart', this.#handleSwipeStart);
    this.refs.dialog?.removeEventListener('touchend', this.#handleSwipeEnd);
    this.#removeClones();
  }

  get scrollContainer() {
    if (this.refs.media && this.refs.media.length > 0) {
      return this.refs.media[0].parentElement;
    }
    return this.refs.dialog;
  }

  /**
   * Fast scroll to an element within the scroll container.
   * @param {HTMLElement} element
   * @returns {Promise<void>}
   */
  #scrollToElement(element) {
    return new Promise((resolve) => {
      const container = this.scrollContainer;
      if (!container || !element) {
        resolve();
        return;
      }

      if (prefersReducedMotion()) {
        element.scrollIntoView({ behavior: 'instant' });
        resolve();
        return;
      }

      const start = container.scrollLeft;
      const target = element.offsetLeft;
      const distance = target - start;

      if (Math.abs(distance) < 1) {
        resolve();
        return;
      }

      container.style.scrollSnapType = 'none';
      container.style.scrollBehavior = 'auto';

      const startTime = performance.now();
      const duration = this.#SCROLL_DURATION;
      /** @param {number} t */
      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

      /** @param {number} now */
      const step = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        container.scrollLeft = start + distance * easeOutCubic(progress);

        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              container.style.scrollSnapType = '';
              container.style.scrollBehavior = '';
              resolve();
            });
          });
        }
      };

      requestAnimationFrame(step);
    });
  }

  // ===========================================
  // Infinite carousel: clone management
  // ===========================================

  #setupClones() {
    const { media } = this.refs;
    if (!media || media.length <= 1) return;

    this.#removeClones();

    const container = this.scrollContainer;
    if (!container || container === this.refs.dialog) return;

    const firstSlide = media[0];
    const lastSlide = media[media.length - 1];
    if (!firstSlide || !lastSlide) return;

    this.#cloneLast = this.#createClone(lastSlide, 'last');
    this.#cloneFirst = this.#createClone(firstSlide, 'first');

    container.prepend(this.#cloneLast);
    container.append(this.#cloneFirst);
  }

  /**
   * @param {HTMLElement} sourceElement
   * @param {string} label
   * @returns {HTMLElement}
   */
  #createClone(sourceElement, label) {
    const clone = /** @type {HTMLElement} */ (sourceElement.cloneNode(true));
    clone.setAttribute('data-clone', label);
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');
    clone.removeAttribute('id');
    clone.removeAttribute('data-media-index');
    clone.style.pointerEvents = 'none';

    // Replace drag-zoom-wrapper custom elements with plain divs
    // to prevent their connectedCallback from firing
    const wrappers = clone.querySelectorAll('drag-zoom-wrapper');
    for (const wrapper of wrappers) {
      const div = document.createElement('div');
      div.className = wrapper.className;
      div.innerHTML = wrapper.innerHTML;
      wrapper.replaceWith(div);
    }

    return clone;
  }

  #removeClones() {
    this.#cloneLast?.remove();
    this.#cloneFirst?.remove();
    this.#cloneLast = null;
    this.#cloneFirst = null;
    this.#isLooping = false;
  }

  #hasClones() {
    return this.#cloneFirst !== null && this.#cloneLast !== null;
  }

  // ===========================================
  // Infinite carousel: loop navigation
  // ===========================================

  /**
   * Smooth-scroll to a clone slide, then instantly jump to the real slide.
   * @param {HTMLElement} cloneElement
   * @param {number} realIndex
   */
  #scrollToCloneAndJump(cloneElement, realIndex) {
    if (this.#isLooping) return;
    this.#isLooping = true;

    this.#updateActiveThumbnail(realIndex);
    this.#updateCounter(realIndex);
    this.dispatchEvent(new ZoomMediaSelectedEvent(realIndex));

    const targetMedia = this.refs.media[realIndex];
    if (targetMedia) {
      this.#loadHighResolutionImage(targetMedia);
    }

    this.#scrollToElement(cloneElement).then(() => {
      this.#jumpToRealSlide(realIndex);
    });
  }

  /**
   * Instantly reposition to a real slide (no animation).
   * @param {number} index
   */
  #jumpToRealSlide(index) {
    const container = this.scrollContainer;
    const targetMedia = this.refs.media[index];
    if (!container || !targetMedia) {
      this.#isLooping = false;
      return;
    }

    container.style.scrollSnapType = 'none';
    container.style.scrollBehavior = 'auto';

    targetMedia.scrollIntoView({ behavior: 'instant' });

    // Double rAF to ensure the browser has repainted before restoring snap/smooth
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        container.style.scrollSnapType = '';
        container.style.scrollBehavior = '';
        this.#isLooping = false;
      });
    });
  }

  /**
   * Wait for the current scroll animation to finish, then invoke the callback.
   * Uses scrollend with a timeout fallback.
   * @param {() => void} callback
   */
  #onScrollEnd(callback) {
    const container = this.scrollContainer;
    if (!container) {
      callback();
      return;
    }

    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let timeout;
    const done = () => {
      clearTimeout(timeout);
      container.removeEventListener('scrollend', onEnd);
      callback();
    };
    const onEnd = () => done();

    container.addEventListener('scrollend', onEnd, { once: true });
    timeout = setTimeout(done, 600);
  }

  /** @param {number} index */
  #updateActiveThumbnail(index) {
    const { thumbnails } = this.refs;
    if (!thumbnails) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    const targetThumbnail = thumbnails.children[index];
    if (targetThumbnail && targetThumbnail instanceof HTMLElement) {
      scrollIntoView(targetThumbnail, {
        ancestor: thumbnails,
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  }

  /** @param {number} index */
  #updateCounter(index) {
    const { counter } = this.refs;
    if (counter && counter instanceof HTMLElement) {
      const current = counter.querySelector('.current');
      if (current) current.textContent = String(index + 1);
    }
  }

  /**
   * Check whether the scroll container is currently resting on a clone slide.
   * @returns {HTMLElement | null} The clone element, or null if on a real slide.
   */
  #getScrolledClone() {
    if (!this.#hasClones()) return null;

    const container = this.scrollContainer;
    if (!container) return null;

    const scrollLeft = container.scrollLeft;
    const containerWidth = container.clientWidth;
    const centerX = scrollLeft + containerWidth / 2;

    for (const child of [this.#cloneLast, this.#cloneFirst]) {
      if (!child) continue;
      const childLeft = child.offsetLeft;
      const childRight = childLeft + child.offsetWidth;
      if (centerX >= childLeft && centerX <= childRight) {
        return child;
      }
    }
    return null;
  }

  // ===========================================
  // Dialog open / close
  // ===========================================

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    if (!this.refs) return;

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      document.documentElement.setAttribute('scroll-lock', '');
      dialog.showModal();
      dialog.focus();

      this.#setupClones();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) {
      open();
      return;
    }

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    if (this.#isLooping) return;

    const { media, thumbnails, counter } = this.refs;

    // If native scroll landed on a clone, instantly jump to the real slide
    const scrolledClone = this.#getScrolledClone();
    if (scrolledClone) {
      const isCloneFirst = scrolledClone.getAttribute('data-clone') === 'first';
      const targetIndex = isCloneFirst ? 0 : media.length - 1;

      this.#jumpToRealSlide(targetIndex);
      this.#updateActiveThumbnail(targetIndex);
      this.#updateCounter(targetIndex);
      if (media[targetIndex]) this.#loadHighResolutionImage(media[targetIndex]);
      this.dispatchEvent(new ZoomMediaSelectedEvent(targetIndex));
      return;
    }

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    if (counter && counter instanceof HTMLElement) {
      const current = counter.querySelector('.current');
      if (current) current.textContent = String(activeIndex + 1);
    }

    this.#loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    // Non-product galleries (for example metafield image galleries) don't have a media-gallery pair target.
    if (!mediaGallery) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const transitionName = `gallery-item`;

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    this.#removeClones();
    document.documentElement.removeAttribute('scroll-lock');
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * Hover triggers immediate preview and selection.
   * @param {number} index - The index of the thumbnail to preview.
   */
  async handleThumbnailPointerEnter(index, event) {
    if (isMobileBreakpoint()) return;
    if (event?.buttons > 0) return;

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Reset zoom on current image before navigating
   */
  #resetCurrentZoom() {
    const currentIndex = this.getCurrentIndex();
    const currentMedia = this.refs.media[currentIndex];
    if (!currentMedia) return;

    const zoomWrapper = currentMedia.querySelector('drag-zoom-wrapper');
    if (zoomWrapper && typeof zoomWrapper.reset === 'function') {
      zoomWrapper.reset();
    }
  }

  /**
   * Handle swipe start for mobile navigation
   * @param {TouchEvent} event
   */
  #handleSwipeStart = (event) => {
    if (!isMobileBreakpoint()) return;
    this.#touchStartX = event.touches[0].clientX;
    this.#touchStartY = event.touches[0].clientY;
  };

  /**
   * Handle swipe end for mobile navigation
   * @param {TouchEvent} event
   */
  #handleSwipeEnd = (event) => {
    if (!isMobileBreakpoint()) return;

    const touchEndX = event.changedTouches[0].clientX;
    const touchEndY = event.changedTouches[0].clientY;
    const deltaX = touchEndX - this.#touchStartX;
    const deltaY = touchEndY - this.#touchStartY;

    // Only trigger if horizontal swipe is dominant and exceeds threshold
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > this.#swipeThreshold) {
      if (deltaX > 0) {
        this.previous();
      } else {
        this.next();
      }
    }
  };

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth', loop: false }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    this.#resetCurrentZoom();

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];
    const targetImage = media[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    if (targetImage) {
      if (options.behavior === 'instant' || prefersReducedMotion()) {
        targetImage.scrollIntoView({ behavior: 'instant' });
      } else {
        this.#scrollToElement(targetImage);
      }

      this.#loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * Selects the previous media item.
   * @param {Event} event - The event that triggered the action.
   */
  previous(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.#resetCurrentZoom();

    const currentIndex = this.getCurrentIndex();
    const totalMedia = this.refs.thumbnails?.children.length ?? this.refs.media.length;

    if (currentIndex === 0 && this.#hasClones() && this.#cloneLast) {
      this.#scrollToCloneAndJump(this.#cloneLast, totalMedia - 1);
      return;
    }

    let newIndex = currentIndex - 1;

    if (newIndex < 0) {
      newIndex = totalMedia - 1;
    }

    this.selectThumbnail(newIndex, { behavior: 'smooth' });
  }

  /**
   * Selects the next media item.
   * @param {Event} event - The event that triggered the action.
   */
  next(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.#resetCurrentZoom();

    const currentIndex = this.getCurrentIndex();
    const totalMedia = this.refs.thumbnails?.children.length ?? this.refs.media.length;

    if (currentIndex === totalMedia - 1 && this.#hasClones() && this.#cloneFirst) {
      this.#scrollToCloneAndJump(this.#cloneFirst, 0);
      return;
    }

    let newIndex = currentIndex + 1;

    if (newIndex >= totalMedia) {
      newIndex = 0;
    }

    this.selectThumbnail(newIndex, { behavior: 'smooth' });
  }

  /**
   * Gets the index of the currently visible media item.
   * @returns {number} The index of the current media.
   */
  getCurrentIndex() {
    // Current active thumbnail logic relying on aria-selected, which is updated on scroll/click
    const thumbnails = this.refs.thumbnails;
    if (!thumbnails) return 0;
    
    const activeThumbnail = thumbnails.querySelector('button[aria-selected="true"]');
    if (!activeThumbnail) return 0;
    
    return Array.from(thumbnails.children).indexOf(activeThumbnail);
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
